<script>
import { mapGetters } from 'vuex';
import AceleresCreateCannedModal from './AceleresCreateCannedModal.vue';
import AceleresConfirmDeleteCannedModal from './AceleresConfirmDeleteCannedModal.vue';

export default {
  name: 'AceleresCannedPopover',
  components: { AceleresCreateCannedModal, AceleresConfirmDeleteCannedModal },
  props: {
    show: { type: Boolean, default: false },
    accountId: { type: [Number, String], required: true },
  },
  emits: ['close', 'insert'],
  data() {
    return {
      search: '',
      showCreate: false,
      editingItem: null,
      confirmDeleteItem: null,
    };
  },
  computed: {
    ...mapGetters({ cannedMessages: 'getCannedResponses' }),
    settingsListUrl() {
      return `/app/accounts/${this.accountId}/settings/canned-response/list`;
    },
    filtered() {
      const q = (this.search || '').trim().toLowerCase();
      const items = Array.isArray(this.cannedMessages)
        ? this.cannedMessages
        : [];
      if (!q) return items;
      return items.filter(c => {
        const s = (c.short_code || '').toLowerCase();
        const t = (c.content || '').toLowerCase();
        return s.includes(q) || t.includes(q);
      });
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.search = '';
        this.fetchCanned();
        if (!this.shouldAvoidKeyboard()) {
          this.$nextTick(() => {
            const el = this.$refs.searchInput;
            if (el && el.focus) el.focus();
          });
        }
        document.addEventListener('keydown', this.onEsc);
      } else {
        document.removeEventListener('keydown', this.onEsc);
      }
    },
  },
  mounted() {
    if (this.show) this.fetchCanned();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onEsc);
  },
  methods: {
    shouldAvoidKeyboard() {
      return window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;
    },
    fetchCanned() {
      this.$store.dispatch('getCannedResponse', { searchKey: '' });
    },
    onEsc(e) {
      if (e.key === 'Escape') this.$emit('close');
    },
    onPick(c) {
      this.$emit('insert', {
        content: c.content || '',
        attachments: Array.isArray(c.attachments) ? c.attachments : [],
        attachmentUrl: c.attachment_url || null,
        attachmentSignedId: c.attachment_signed_id || null,
        fileName: c.file_name || '',
        fileType: c.file_type || '',
        fileSize: c.file_size || 0,
      });
      this.$emit('close');
    },
    onEdit(c, e) {
      e.stopPropagation();
      // Aceleres: abre o mesmo modal em modo "editar" pré-preenchido com o item.
      this.editingItem = c;
      this.showCreate = true;
    },
    onDelete(c, e) {
      e.stopPropagation();
      this.confirmDeleteItem = c;
    },
    onConfirmDeleteClose() {
      this.confirmDeleteItem = null;
    },
    onCannedDeleted() {
      this.fetchCanned();
    },
    onAddNew() {
      this.editingItem = null;
      this.showCreate = true;
    },
    onCreateClose() {
      this.showCreate = false;
      this.editingItem = null;
    },
    onCreateSaved() {
      // Refetch para que a nova/editada resposta aparece atualizada no listado.
      this.fetchCanned();
    },
    preview(text) {
      const s = (text || '').replace(/\s+/g, ' ');
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    },
  },
};
</script>

<template>
  <div v-if="show" class="acl-canned-backdrop" @click.self="$emit('close')">
    <div
      v-show="!showCreate && !confirmDeleteItem"
      class="acl-canned-popover"
      role="dialog"
      aria-label="Respostas rápidas"
    >
      <header class="acl-canned-header">
        <span class="acl-canned-title">Respostas rápidas</span>
        <button
          type="button"
          class="acl-canned-close"
          aria-label="Fechar"
          @click="$emit('close')"
        >
          <i class="i-ph-x" />
        </button>
      </header>

      <div class="acl-canned-search-wrap">
        <i class="i-ph-magnifying-glass acl-canned-search-icon" />
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="acl-canned-search"
          placeholder="Buscar por atalho ou texto…"
        />
      </div>

      <div v-if="filtered.length === 0" class="acl-canned-empty">
        <p v-if="search">Nenhuma resposta encontrada para "{{ search }}".</p>
        <p v-else>
          Você ainda não tem respostas rápidas.<br />
          Crie a primeira para usar com um clique.
        </p>
        <button type="button" class="acl-canned-primary" @click="onAddNew">
          <i class="i-ph-plus" /> Criar resposta rápida
        </button>
      </div>

      <ul v-else class="acl-canned-list">
        <li
          v-for="c in filtered"
          :key="c.id || c.short_code"
          class="acl-canned-item"
          @click="onPick(c)"
        >
          <div class="acl-canned-item-main">
            <span class="acl-canned-short">/{{ c.short_code }}</span>
            <span class="acl-canned-preview">{{ preview(c.content) }}</span>
          </div>
          <span
            v-if="(c.attachments && c.attachments.length) || c.attachment_url"
            class="acl-canned-attachment-count"
          >
            <i class="i-ph-paperclip" />
            {{
              c.attachments && c.attachments.length ? c.attachments.length : 1
            }}
          </span>
          <div class="acl-canned-actions">
            <button
              type="button"
              class="acl-canned-edit"
              aria-label="Editar resposta rápida"
              @click="onEdit(c, $event)"
            >
              <i class="i-ph-pencil-simple" />
            </button>
            <button
              type="button"
              class="acl-canned-delete"
              aria-label="Excluir resposta rápida"
              @click="onDelete(c, $event)"
            >
              <i class="i-ph-trash" />
            </button>
          </div>
        </li>
      </ul>

      <footer v-if="filtered.length > 0" class="acl-canned-footer">
        <button type="button" class="acl-canned-add" @click="onAddNew">
          <i class="i-ph-plus" /> Nova resposta rápida
        </button>
      </footer>
    </div>

    <AceleresCreateCannedModal
      :show="showCreate"
      :editing="editingItem"
      @close="onCreateClose"
      @saved="onCreateSaved"
    />

    <AceleresConfirmDeleteCannedModal
      :show="!!confirmDeleteItem"
      :canned-response="confirmDeleteItem"
      @close="onConfirmDeleteClose"
      @deleted="onCannedDeleted"
    />
  </div>
</template>

<style lang="scss" scoped>
.acl-canned-backdrop {
  position: absolute;
  inset: 0;
  z-index: 500;
  pointer-events: none;
}

.acl-canned-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 12px;
  width: 480px;
  max-width: calc(100vw - 24px);
  max-height: 560px;
  display: flex;
  flex-direction: column;
  background: var(--white, #ffffff);
  border: 1px solid var(--s-100, #e2e8f0);
  border-radius: 14px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.2);
  pointer-events: auto;
  overflow: hidden;
}

.acl-canned-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--s-100, #e2e8f0);
}

.acl-canned-title {
  font-weight: 600;
  font-size: 16px;
  color: var(--s-800, #1e293b);
}

.acl-canned-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: var(--s-500, #64748b);

  &:hover {
    background: var(--s-50, #f8fafc);
    color: var(--s-700, #334155);
  }

  i {
    font-size: 18px;
  }
}

.acl-canned-search-wrap {
  position: relative;
  padding: 12px 18px;
  border-bottom: 1px solid var(--s-50, #f1f5f9);
}

.acl-canned-search-icon {
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: var(--s-400, #94a3b8);
  pointer-events: none;
}

.acl-canned-search {
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid var(--s-200, #e2e8f0);
  border-radius: 10px;
  font-size: 14px;
  background: var(--s-25, #fafbfc);
  color: var(--s-800, #1e293b);
  outline: none;

  &:focus {
    border-color: var(--w-500, #3b82f6);
    background: var(--white, #fff);
  }
}

.acl-canned-list {
  margin: 0;
  padding: 6px 0;
  list-style: none;
  overflow-y: auto;
  flex: 1 1 auto;
}

.acl-canned-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: var(--s-50, #f8fafc);
  }
}

.acl-canned-item-main {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.acl-canned-short {
  font-weight: 600;
  font-size: 15px;
  color: var(--s-800, #1e293b);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.acl-canned-preview {
  font-size: 13.5px;
  color: var(--s-500, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 4px;
  line-height: 1.4;
}

.acl-canned-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.acl-canned-attachment-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--s-500, #64748b);
  flex-shrink: 0;
}

.acl-canned-edit,
.acl-canned-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: var(--s-400, #94a3b8);
  flex-shrink: 0;

  i {
    font-size: 18px;
  }
}

.acl-canned-edit:hover {
  background: var(--w-50, #eff6ff);
  color: var(--w-600, #2563eb);
}

.acl-canned-delete:hover {
  background: rgba(220, 38, 38, 0.1);
  color: var(--r-600, #dc2626);
}

.acl-canned-empty {
  padding: 32px 24px;
  text-align: center;
  color: var(--s-500, #64748b);
  font-size: 14.5px;

  p {
    margin: 0 0 16px;
    line-height: 1.5;
  }
}

.acl-canned-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  border: 0;
  background: #00a884;
  color: #fff;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #008f6f;
  }
  i {
    font-size: 16px;
  }
}

.acl-canned-footer {
  padding: 12px 18px;
  border-top: 1px solid var(--s-100, #e2e8f0);
  display: flex;
  justify-content: flex-end;
}

.acl-canned-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: 1px dashed var(--s-300, #cbd5e1);
  background: transparent;
  color: var(--s-700, #334155);
  border-radius: 999px;
  font-size: 13.5px;
  cursor: pointer;

  &:hover {
    border-color: #00a884;
    color: #00a884;
    background: rgba(0, 168, 132, 0.06);
  }

  i {
    font-size: 15px;
  }
}
</style>
